import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCountriesDataView } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries, {loadCurrentCountriesParameter} from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { loadPageLinks } from "@insite/client-framework/Store/Links/LinksActionCreators";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select from "@insite/mobius/Select/Select";
import TextField from "@insite/mobius/TextField";
import Toaster from "@insite/mobius/Toast/Toaster";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import PhoneNumberField, { PhoneNumberState } from "@preferredMedical/Components/Fields/PhoneNumberField";
import { EmailSubscriptionSignUpRequest } from "@preferredMedical/Models/Header/EmailSubscriptionSignUpRequest";
import { EventEx } from "@preferredMedical/Models/Misc/EventEx";
import { sendEmailSubscriptionSignUp } from "@preferredMedical/Services/CustomFormsService";
import { emailField, requiredStringField } from "@preferredMedical/Services/FormValidation";
import { generateSelectOptions } from "@preferredMedical/Services/SelectHelper";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    emailRecipients: string;
    classesOfTrade: string;
    title: string;
    description: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const countriesDataView = getCountriesDataView(state, loadCurrentCountriesParameter);

    return {
        countriesDataView,
        shouldLoadCountries: !countriesDataView.value && !countriesDataView.isLoading,
        links: state.links
    };
};

const mapDispatchToProps = {
    loadCurrentCountries,
    loadPageLinks
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface EmailSubscriptionSignUpStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    oneLinerGridItem?: GridItemProps;
    twoLinerGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
}

export interface EmailSubscriptionSignUpFormData {
    fullName: string;
    companyName: string;
    email: string;
    phone: string;
    state: string;
    classOfTrade: string;

    phoneOnChange: EventEx;
    phoneUpdate: EventEx;
}

export interface EmailSubscriptionSignUpFormErrors {
    fullName: string;
    companyName: string;
    email: string;
    phone: string;
    state: string;
    classOfTrade: string;
}

const initialFormErrors: EmailSubscriptionSignUpFormErrors = {
    fullName: "",
    companyName: "",
    email: "",
    phone: "",
    state: "",
    classOfTrade: ""
};

const initialFormData : EmailSubscriptionSignUpFormData = {
    fullName: "",
    companyName: "",
    email: "",
    phone: "",
    state: "",
    classOfTrade: "",

    phoneOnChange: new EventEx(),
    phoneUpdate: new EventEx(),
};

export const styles: EmailSubscriptionSignUpStyles = {
    modal: {
        size: 800,
        cssOverrides: {
            modalTitle: css`
                border-bottom: 0;
                padding-bottom: 0px;
                justify-content: center;
            `,
            titleButton: css`
                display: none;
            `,
            modalContent: css`
                padding: 20px 30px;
            `,
        },
    },
    container: { 
        gap: 10,
        css: css`
            text-align: left;
        `
    },
    oneLinerGridItem: {
        width: 12,
        css: css`
            padding-left: 10px;
            justify-content: center;
        `,
    },
    twoLinerGridItem: {
        width: 6,
        css: css`
            padding-left: 10px;
        `,
    },
    submitButton: {
        css: css`
            width: 100%;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            width: 100%;
        `,
    },
};

export interface EmailSubscriptionSignUpState {
    inProgress: boolean,
    modalIsOpen: boolean,
    formData: EmailSubscriptionSignUpFormData,
    formErrors : EmailSubscriptionSignUpFormErrors
}

const requiredFieldMessage = translate("This is a required field.");
const emailFieldMessage = translate("Enter a valid email address.");

class EmailSubscriptionSignUpModal extends React.Component<Props, EmailSubscriptionSignUpState> {
    constructor(props: Props) {
        super(props);

        const {
            shouldLoadCountries,
            loadCurrentCountries,
        } = this.props;

        if (shouldLoadCountries) {
            loadCurrentCountries();
        }

        if (this.props.links.pageLinks.length === 0 && !this.props.links.areLinksLoading) {
            loadPageLinks();
        }

        this.state = {
            inProgress: false,
            modalIsOpen: true,
            formData: {...initialFormData},
            formErrors: {...initialFormErrors}
        };

        this.state.formData.phoneOnChange.subscribe(async (phoneNumberState: PhoneNumberState) => {
            await this.setState({...this.state, formData: {...this.state.formData, phone: phoneNumberState.value}});
            await this.setState({...this.state, formErrors: {...this.state.formErrors, phone: phoneNumberState.error}});
        });

        this.getUSStatesNames = this.getUSStatesNames.bind(this);
        this.checkForErrors = this.checkForErrors.bind(this);
        this.submitButtonClickHandler = this.submitButtonClickHandler.bind(this);
        this.closeModal = this.closeModal.bind(this);
    }

    getUSStatesNames = () => {
        if(!this.props.countriesDataView || !this.props.countriesDataView.value)
            {return [];}

        const unitedStatesCountry = this.props.countriesDataView.value.find(x => x.abbreviation === "US");
        return unitedStatesCountry?.states?.map(x => x.name) ?? [];
    }

    checkForErrors = async () => {     
        let hasError = false;
        const currentFormErrors: EmailSubscriptionSignUpFormErrors = {...initialFormErrors};
        const formData = this.state.formData;

        ({hasError, errorMessage: currentFormErrors.fullName} = requiredStringField(formData.fullName, requiredFieldMessage, hasError));
        ({hasError, errorMessage: currentFormErrors.companyName} = requiredStringField(formData.companyName, requiredFieldMessage, hasError));
        ({hasError, errorMessage: currentFormErrors.state} = requiredStringField(formData.state, requiredFieldMessage, hasError));
        ({hasError, errorMessage: currentFormErrors.classOfTrade} = requiredStringField(formData.classOfTrade, requiredFieldMessage, hasError));
        
        ({hasError, errorMessage: currentFormErrors.email} = emailField(formData.email, emailFieldMessage, hasError));

        await this.state.formData.phoneUpdate.emitAsync();
        if (this.state.formErrors.phone) {
            hasError = true;
        }

        await this.setState({...this.state, formErrors: currentFormErrors});
        return hasError;
    };

    submitButtonClickHandler = async (addToast: Function) => {
        const successToast = {
            body: "Email subscription successful",
            messageType: "info",
        };

        const hasErrors = await this.checkForErrors();

        if (hasErrors) {
            return;
        }
        
        this.setState({...this.state, inProgress: true});

        const model : EmailSubscriptionSignUpRequest = {
            ...this.state.formData,
            emailTos: this.props.emailRecipients.split("\n")
        };

        sendEmailSubscriptionSignUp(model);
        addToast(successToast);
        this.closeModal();
    };

    closeModal = () => {
        this.setState({...this.state, modalIsOpen: false});
    }

    goToHomepage = () => {
        this.closeModal();
        window.location.href = this.props.links.pageLinks[0].url ?? "/";
    }

    render() {

        const classesOfTradeOptions = generateSelectOptions(this.props.classesOfTrade.split("\n"), true, "Select Class of Trade");
        const usStatesOptions = generateSelectOptions(this.getUSStatesNames(), true, "Select A State");

        return (
            <Toaster>
                <ToasterContext.Consumer >
                    {({ addToast }) => (
                        <Modal
                            {...styles.modal}
                            headline={this.props.title}
                            isOpen={this.state.modalIsOpen}
                            handleClose={this.closeModal}
                        >
                            <GridContainer {...styles.container} data-test-selector="emailSubscriptionSignUpForm">
                                <GridItem {...styles.oneLinerGridItem}>
                                    <div>
                                        <Typography>{this.props.description}</Typography>
                                    </div>
                                </GridItem>
                                <GridItem {...styles.twoLinerGridItem}>
                                    <TextField
                                        label={translate("Full Name")}
                                        value={this.state.formData.fullName}
                                        onChange={(e) => this.setState({...this.state, formData: {...this.state.formData, fullName: e.target.value}})}
                                        data-test-selector="emailSubscriptionSignUpFullName"
                                        error={this.state.formErrors.fullName}
                                        required={true}
                                    />
                                </GridItem>

                                <GridItem {...styles.twoLinerGridItem}>
                                    <TextField
                                        label={translate("Company Name")}
                                        value={this.state.formData.companyName}
                                        onChange={(e) => this.setState({...this.state, formData: {...this.state.formData, companyName: e.target.value}})}
                                        data-test-selector="emailSubscriptionSignUpCompanyName"
                                        error={this.state.formErrors.companyName}
                                        required={true}
                                    />
                                </GridItem>

                                <GridItem {...styles.twoLinerGridItem}>
                                    <TextField
                                        label={translate("Email")}
                                        value={this.state.formData.email}
                                        onChange={(e) => this.setState({...this.state, formData: {...this.state.formData, email: e.target.value}})}
                                        data-test-selector="emailSubscriptionSignUpEmail"
                                        error={this.state.formErrors.email}
                                        required={true}
                                    />
                                </GridItem>

                                <GridItem {...styles.twoLinerGridItem}>
                                    <PhoneNumberField
                                        label={translate("Phone")}
                                        initialValue={this.state.formData.phone}
                                        onChange={this.state.formData.phoneOnChange}
                                        update={this.state.formData.phoneUpdate}
                                        data-test-selector="emailSubscriptionSignUpPhone"
                                        format="000-000-0000"
                                        required={true}
                                        hideRequiredAsterisk={false}
                                    />
                                </GridItem>

                                <GridItem {...styles.twoLinerGridItem}>
                                <Select
                                        label={translate(`State`)}
                                        required={true}
                                        error={this.state.formErrors.state}
                                        onChange={(e) => this.setState({...this.state, formData: {...this.state.formData, state: e.target.value}})}
                                        value={this.state.formData.state}
                                    >
                                        {usStatesOptions}
                                    </Select>
                                </GridItem>

                                <GridItem {...styles.twoLinerGridItem}>
                                    <Select
                                        label={translate(`Class Of Trade`)}
                                        required={true}
                                        error={this.state.formErrors.classOfTrade}
                                        onChange={(e) => this.setState({...this.state, formData: {...this.state.formData, classOfTrade: e.target.value}})}
                                        value={this.state.formData.classOfTrade}
                                    >
                                        {classesOfTradeOptions}
                                    </Select>
                                </GridItem>

                                <GridItem {...styles.oneLinerGridItem}>
                                    <Button
                                        {...styles.submitButton}
                                        onClick={() => this.submitButtonClickHandler(addToast)}
                                        data-test-selector="emailSubscriptionSignUpSubmit"
                                        disabled={this.state.inProgress}
                                    >
                                        {translate("Submit")}
                                    </Button>
                                </GridItem>
                                <GridItem {...styles.oneLinerGridItem}>
                                    <Button
                                        {...styles.cancelButton}
                                        onClick={this.goToHomepage}
                                        data-test-selector="emailSubscriptionSignUpCancel"
                                    >
                                        {translate("Don't Show This Message Again")}
                                    </Button>
                                </GridItem>
                            </GridContainer>
                        </Modal>
                )}
                </ToasterContext.Consumer>
            </Toaster>);
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(EmailSubscriptionSignUpModal);